/**
 * TimerWidget — Componente del temporizador para el Dashboard.
 * 
 * Permite seleccionar un cliente e iniciar/pausar/detener el timer.
 */

import { useEffect, useState } from 'react';
import { Play, Pause, Square, Clock, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTimerStore, getTotalElapsed } from '@/stores/timerStore';
import { api } from '@/lib/api';
import type { Client } from '@/types';

export function TimerWidget() {
  const timer = useTimerStore();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [, setTick] = useState(0);

  // Load clients for selector
  useEffect(() => {
    api.getClients().then((res) => setClients(res.clients as Client[]));
  }, []);

  // Tick timer display
  useEffect(() => {
    if (!timer.isRunning) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [timer.isRunning]);

  const elapsed = getTotalElapsed(timer);
  const isActive = timer.isRunning || timer.elapsed > 0;

  const handleStart = () => {
    const client = clients.find((c) => c.id === selectedClient);
    if (client) {
      timer.start(client.id, client.name);
    }
  };

  return (
    <Card className={timer.isRunning ? 'border-primary/50 shadow-primary/10 shadow-lg' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock size={20} className={timer.isRunning ? 'text-primary animate-pulse' : 'text-muted-foreground'} />
          Temporizador
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timer display */}
        <div className="text-center py-4">
          <p className="text-4xl font-mono font-bold tracking-wider text-foreground">
            {String(Math.floor(elapsed / 3600)).padStart(2, '0')}:
            {String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0')}:
            {String(elapsed % 60).padStart(2, '0')}
          </p>
          {timer.clientName && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1">
              <User size={14} />
              {timer.clientName}
            </p>
          )}
        </div>

        {/* Client selector (when not active) */}
        {!isActive && (
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            <option value="">Seleccionar cliente...</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {/* Description input */}
        {isActive && (
          <input
            type="text"
            placeholder="¿En qué estás trabajando?"
            value={timer.description}
            onChange={(e) => timer.setDescription(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        )}

        {/* Controls */}
        <div className="flex gap-2 justify-center">
          {!isActive ? (
            <Button
              onClick={handleStart}
              disabled={!selectedClient}
              className="gap-2"
            >
              <Play size={16} />
              Iniciar
            </Button>
          ) : (
            <>
              {timer.isRunning ? (
                <Button variant="outline" onClick={timer.pause} className="gap-2">
                  <Pause size={16} />
                  Pausar
                </Button>
              ) : (
                <Button onClick={timer.resume} className="gap-2">
                  <Play size={16} />
                  Continuar
                </Button>
              )}
              <Button
                variant="destructive"
                onClick={timer.stop}
                className="gap-2"
              >
                <Square size={16} />
                Detener
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
